import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { grafikService } from '../../services/grafikService';
import { toISODate } from '../../utils/grafikHelpers';

/**
 * Modal: zaznacz zbiorczo dostepnosc dla zakresu dat + dni tygodnia.
 * Typ: dostepny / niedostepny / preferowany.
 * Po zapisie wywoluje onSaved() i onClose().
 */
const TYPES = [
  { value: 'dostepny', label: 'Dostepny', color: 'var(--success)' },
  { value: 'niedostepny', label: 'Niedostepny', color: 'var(--danger)' },
  { value: 'preferowany', label: 'Preferowany', color: 'var(--info)' }
];

const WEEKDAYS = [
  { dt: 1, label: 'Pn' },
  { dt: 2, label: 'Wt' },
  { dt: 3, label: 'Sr' },
  { dt: 4, label: 'Cz' },
  { dt: 5, label: 'Pt' },
  { dt: 6, label: 'So' },
  { dt: 7, label: 'Nd' }
];

const BulkAvailabilityModal = ({ initialFrom, initialTo, onClose, onSaved }) => {
  const today = new Date();
  const defaultFrom = initialFrom || toISODate(today);
  const defaultTo = initialTo || toISODate(new Date(today.getFullYear(), today.getMonth() + 1, 0)); // koniec miesiaca

  const [dataOd, setDataOd] = useState(defaultFrom);
  const [dataDo, setDataDo] = useState(defaultTo);
  const [selectedDays, setSelectedDays] = useState({ 1: true, 2: true, 3: true, 4: true, 5: true, 6: false, 7: false });
  const [typ, setTyp] = useState('niedostepny');
  const [godzinaOd, setGodzinaOd] = useState('');
  const [godzinaDo, setGodzinaDo] = useState('');
  const [notatka, setNotatka] = useState('');
  const [override, setOverride] = useState(true);
  const [saving, setSaving] = useState(false);

  const toggleDay = (dt) => {
    setSelectedDays(prev => ({ ...prev, [dt]: !prev[dt] }));
  };

  const setAllDays = (val) => {
    setSelectedDays({ 1: val, 2: val, 3: val, 4: val, 5: val, 6: val, 7: val });
  };

  // Liczba dni do dodania (preview)
  const previewCount = useMemo(() => {
    if (!dataOd || !dataDo) return 0;
    try {
      const from = new Date(dataOd + 'T00:00:00');
      const to = new Date(dataDo + 'T00:00:00');
      if (to < from) return 0;
      let count = 0;
      const d = new Date(from);
      const guard = 400;
      let i = 0;
      while (d <= to && i < guard) {
        const jsDay = d.getDay();
        const dt = jsDay === 0 ? 7 : jsDay;
        if (selectedDays[dt]) count++;
        d.setDate(d.getDate() + 1);
        i++;
      }
      return count;
    } catch {
      return 0;
    }
  }, [dataOd, dataDo, selectedDays]);

  const handleSave = async () => {
    if (!dataOd || !dataDo) {
      toast.error('Wybierz zakres dat');
      return;
    }
    if (dataDo < dataOd) {
      toast.error('Data do musi byc po dacie od');
      return;
    }
    const dni = WEEKDAYS.filter(w => selectedDays[w.dt]).map(w => w.dt);
    if (dni.length === 0) {
      toast.error('Wybierz co najmniej 1 dzien tygodnia');
      return;
    }
    if (previewCount === 0) {
      toast.error('Brak dni pasujacych do zakresu i wybranych dni tygodnia');
      return;
    }

    setSaving(true);
    try {
      const res = await grafikService.bulkMyAvailability({
        data_od: dataOd,
        data_do: dataDo,
        dni_tygodnia: dni,
        typ,
        godzina_od: godzinaOd || undefined,
        godzina_do: godzinaDo || undefined,
        notatka: notatka || undefined,
        override_existing: override
      });
      const inserted = res?.data?.inserted ?? previewCount;
      const skipped = res?.data?.skipped ?? 0;
      toast.success(skipped > 0
        ? `Zapisano ${inserted} dni (${skipped} pominietych)`
        : `Zapisano ${inserted} dni`);
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      toast.error(err.message || 'Blad zapisu zbiorczego');
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem', color: 'var(--text-muted)' };
  const inputStyle = {
    width: '100%', padding: '0.4rem 0.5rem',
    border: '1px solid var(--border)', borderRadius: '0.375rem',
    backgroundColor: 'var(--card-bg)', color: 'var(--text)',
    fontSize: '0.85rem'
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'var(--card-bg)', borderRadius: '0.75rem',
        maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          padding: '1rem 1.5rem', borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, var(--info) 0%, var(--info) 100%)',
          color: '#fff', borderRadius: '0.75rem 0.75rem 0 0'
        }}>
          <h5 style={{ margin: 0, fontWeight: 600 }}>
            <i className="fas fa-calendar-check" style={{ marginRight: '0.5rem' }}></i>
            Zaznacz zbiorczo dostepnosc
          </h5>
          <button onClick={onClose}
            style={{
              background: 'transparent', border: 'none', color: '#fff',
              fontSize: '1.5rem', cursor: 'pointer', padding: 0, lineHeight: 1
            }}>&times;</button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {/* Typ */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Typ dostepnosci</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {TYPES.map(t => (
                <button
                  key={t.value}
                  onClick={() => setTyp(t.value)}
                  style={{
                    flex: 1, padding: '6px 12px', fontSize: '12px',
                    border: typ === t.value ? `2px solid ${t.color}` : '1px solid var(--border)',
                    backgroundColor: typ === t.value ? t.color.replace(')', '-light)') : 'var(--card-bg)',
                    color: typ === t.value ? t.color : 'var(--text-secondary)',
                    fontWeight: typ === t.value ? 700 : 500,
                    cursor: 'pointer'
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Zakres dat */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={labelStyle}>Data od</label>
              <input type="date" value={dataOd} onChange={(e) => setDataOd(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Data do</label>
              <input type="date" value={dataDo} onChange={(e) => setDataDo(e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* Dni tygodnia */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Dni tygodnia</label>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
              {WEEKDAYS.map(w => (
                <button
                  key={w.dt}
                  onClick={() => toggleDay(w.dt)}
                  style={{
                    minWidth: '48px', padding: '6px 12px', fontSize: '12px',
                    border: selectedDays[w.dt] ? '2px solid var(--info)' : '1px solid var(--border)',
                    backgroundColor: selectedDays[w.dt] ? 'var(--info-light)' : 'var(--card-bg)',
                    color: selectedDays[w.dt] ? 'var(--info)' : 'var(--text-secondary)',
                    fontWeight: selectedDays[w.dt] ? 700 : 500, cursor: 'pointer'
                  }}
                >
                  {w.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button onClick={() => setAllDays(true)}
                style={{
                  padding: '4px 10px', fontSize: '11px',
                  background: 'transparent', border: '1px solid var(--border)',
                  color: 'var(--text-muted)', cursor: 'pointer'
                }}>
                Zaznacz wszystkie
              </button>
              <button onClick={() => setAllDays(false)}
                style={{
                  padding: '4px 10px', fontSize: '11px',
                  background: 'transparent', border: '1px solid var(--border)',
                  color: 'var(--text-muted)', cursor: 'pointer'
                }}>
                Odznacz wszystkie
              </button>
              <button onClick={() => setSelectedDays({ 1: true, 2: true, 3: true, 4: true, 5: true, 6: false, 7: false })}
                style={{
                  padding: '4px 10px', fontSize: '11px',
                  background: 'transparent', border: '1px solid var(--border)',
                  color: 'var(--text-muted)', cursor: 'pointer'
                }}>
                Tylko Pn-Pt
              </button>
              <button onClick={() => setSelectedDays({ 1: false, 2: false, 3: false, 4: false, 5: false, 6: true, 7: true })}
                style={{
                  padding: '4px 10px', fontSize: '11px',
                  background: 'transparent', border: '1px solid var(--border)',
                  color: 'var(--text-muted)', cursor: 'pointer'
                }}>
                Tylko weekend
              </button>
            </div>
          </div>

          {/* Godziny opcjonalne */}
          <details style={{ marginBottom: '1rem', padding: '0.5rem 0.75rem', backgroundColor: 'var(--bg)', borderRadius: '0.375rem' }}>
            <summary style={{ cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
              Godziny i notatka (opcjonalne)
            </summary>
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <div>
                  <label style={labelStyle}>Godzina od</label>
                  <input type="time" value={godzinaOd} onChange={(e) => setGodzinaOd(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Godzina do</label>
                  <input type="time" value={godzinaDo} onChange={(e) => setGodzinaDo(e.target.value)} style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Notatka</label>
                <input type="text" value={notatka} onChange={(e) => setNotatka(e.target.value)}
                  placeholder='np. "egzamin", "L4", ...'
                  style={inputStyle} />
              </div>
            </div>
          </details>

          {/* Override checkbox */}
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
            padding: '0.6rem 0.75rem', backgroundColor: 'var(--bg)',
            borderRadius: '0.375rem', cursor: 'pointer', marginBottom: '1rem'
          }}>
            <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)}
              style={{ marginTop: '2px' }} />
            <div style={{ fontSize: '0.8rem' }}>
              <div style={{ fontWeight: 600, color: 'var(--text)' }}>Zastap istniejace wpisy</div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                Jesli odznaczone, dni z istniejaca dostepnoscia zostana pominiete
              </div>
            </div>
          </label>

          {/* Preview */}
          <div style={{
            padding: '0.75rem', backgroundColor: 'var(--info)15',
            border: '1px solid var(--info)', borderRadius: '0.375rem',
            fontSize: '0.85rem', color: 'var(--text)'
          }}>
            <i className="fas fa-info-circle" style={{ marginRight: '0.4rem', color: 'var(--info)' }} />
            Podglad: <strong>{previewCount}</strong> {previewCount === 1 ? 'dzien' : 'dni'} do oznaczenia jako <strong>{TYPES.find(t => t.value === typ)?.label}</strong>
          </div>
        </div>

        <div style={{
          padding: '1rem 1.5rem', borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'flex-end', gap: '0.5rem',
          backgroundColor: 'var(--bg)', borderRadius: '0 0 0.75rem 0.75rem'
        }}>
          <button onClick={onClose} disabled={saving}
            style={{
              padding: '6px 12px', fontSize: '12px',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)',
              cursor: 'pointer', fontWeight: 500
            }}>
            Anuluj
          </button>
          <button onClick={handleSave} disabled={saving || previewCount === 0}
            style={{
              padding: '6px 12px', fontSize: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, var(--info) 0%, var(--info) 100%)',
              color: '#fff', cursor: previewCount === 0 ? 'not-allowed' : 'pointer',
              fontWeight: 600, opacity: previewCount === 0 ? 0.5 : 1
            }}>
            <i className="fas fa-save" style={{ marginRight: '0.4rem' }} />
            {saving ? 'Zapisywanie...' : `Zapisz ${previewCount} dni`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAvailabilityModal;
